"use strict";

/**
 * DGF-4906 — types example searches into the header search box's placeholder, one after another, to show that
 * search takes whole questions as well as keywords. The phrases come from the sdSearchPlaceholderPhrases preference
 * (components/header/search.isml); with none, the input has no data-placeholder-phrases and this does nothing.
 *
 * The placeholder is only a visual hint. aria-label and aria-placeholder stay fixed on "Search for products...", so
 * the field isn't re-announced as it types.
 */

const TYPE_MS = 60;
const DELETE_MS = 20;
const HOLD_MS = 2000;
const GAP_MS = 400;

/**
 * Cycles through the phrases: types one out, holds it, deletes it, moves to the next
 * @param {string[]} phrases - the phrases, at least one
 * @param {Function} setPlaceholder - called with the text to show
 * @returns {{start: Function, stop: Function}} controls; start resumes from where it stopped
 */
function createTypewriter(phrases, setPlaceholder) {
    let phrase = 0;
    let length = 0;
    let deleting = false;
    let timer = null;

    const tick = () => {
        const text = phrases[phrase];
        let delay;

        if (!deleting) {
            length += 1;
            delay = length < text.length ? TYPE_MS : HOLD_MS;
            deleting = length >= text.length;
        } else {
            length -= 1;
            delay = length > 0 ? DELETE_MS : GAP_MS;
            if (length === 0) {
                deleting = false;
                phrase = (phrase + 1) % phrases.length;
            }
        }

        setPlaceholder(text.slice(0, length));
        timer = setTimeout(tick, delay);
    };

    return {
        start: () => {
            if (timer === null) {
                timer = setTimeout(tick, length === 0 ? GAP_MS : TYPE_MS);
            }
        },
        stop: () => {
            clearTimeout(timer);
            timer = null;
        }
    };
}

/**
 * The end of the text that fits the box. A placeholder can't be scrolled, so the leading characters are dropped
 * instead: the text moves left as it types past the edge, like typing into a full field, and back as it deletes.
 * @param {string} text - the text to show
 * @param {Function} fits - whether a string fits the box
 * @returns {string} the longest end of the text that fits
 */
function visibleEnd(text, fits) {
    let start = 0;
    while (start < text.length && !fits(text.slice(start))) {
        start += 1;
    }
    // A space left at the front would show as a gap against the box's edge
    return start ? text.slice(start).trimStart() : text;
}

/**
 * Measures text in the input's font against the room left inside its padding
 * @param {HTMLInputElement} input - the search input
 * @returns {Function} called once per frame: returns whether a string fits the box, or null when there's nothing to
 *     measure against (field hidden, or no canvas) - the phrase is then shown whole
 */
function measureInput(input) {
    const context = document.createElement("canvas").getContext("2d");

    return () => {
        if (!context || !input.clientWidth) {
            return null;
        }

        // Read once per frame, not per candidate string: the padding changes as the clear cross shows and hides,
        // and the width with the viewport
        const style = window.getComputedStyle(input);
        const room = input.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        if (room <= 0) {
            return null;
        }
        const spacing = parseFloat(style.letterSpacing) || 0;
        // Built from longhands: Firefox can return "" for the computed font shorthand, which the canvas ignores,
        // leaving its 10px default - under-measuring, so long phrases would be clipped rather than scrolled
        context.font = [style.fontStyle, style.fontWeight, style.fontSize, style.fontFamily].join(" ");
        return text => context.measureText(text).width + spacing * text.length <= room;
    };
}

/**
 * @param {string} json - the data-placeholder-phrases value
 * @returns {string[]} the phrases, or empty if the value isn't a usable list
 */
function parsePhrases(json) {
    try {
        const phrases = JSON.parse(json);
        return Array.isArray(phrases) ? phrases.filter(phrase => typeof phrase === "string" && phrase.trim()) : [];
    } catch (e) {
        return [];
    }
}

module.exports = function () {
    document.querySelectorAll("input.search-field[data-placeholder-phrases]").forEach(input => {
        const phrases = parsePhrases(input.getAttribute("data-placeholder-phrases"));
        if (!phrases.length) {
            return;
        }

        // Reduced motion: the first phrase, still, so the hint is there without the animation. Put back on blur, as
        // search.js clears the placeholder on focus and its outside-click reset skips fields with phrases.
        if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            const showFirstPhrase = () => input.setAttribute("placeholder", phrases[0]);
            showFirstPhrase();
            input.addEventListener("blur", showFirstPhrase);
            return;
        }

        const measure = measureInput(input);
        const typewriter = createTypewriter(phrases, text => {
            const fits = measure();
            input.setAttribute("placeholder", fits ? visibleEnd(text, fits) : text);
        });
        const isIdle = () => document.visibilityState !== "hidden" && document.activeElement !== input && !input.value;
        const update = () => (isIdle() ? typewriter.start() : typewriter.stop());

        // Stops while the shopper is in the field (search.js clears the placeholder on focus) or has typed, and
        // while the tab is hidden; picks up again once the field is empty and left
        input.addEventListener("focus", update);
        input.addEventListener("blur", update);
        input.addEventListener("input", update);
        // The clear cross is a reset button: focus leaves the field while it still has text, then the reset empties
        // it without an input event. Checked once the reset has happened, as the event fires before it.
        if (input.form) {
            input.form.addEventListener("reset", () => setTimeout(update, 0));
        }
        document.addEventListener("visibilitychange", update);
        update();
    });
};

module.exports.createTypewriter = createTypewriter;
module.exports.parsePhrases = parsePhrases;
module.exports.visibleEnd = visibleEnd;
